import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism"
import type { Components } from "react-markdown"
import { cn } from "@/lib/utils"
import {
  DIFF_HIGHLIGHT_CLASS,
  isNodeChanged,
  type ChangedRange,
  type NodePosition,
} from "../lib/diffRangeHighlight"
import { EditableParagraph, SectionEditContext, type DocumentEditing } from "./EditableParagraph"

interface DocumentRendererProps {
  content: string
  className?: string
  /**
   * Line ranges (1-indexed, inclusive) that changed in the most recent
   * rewrite commit. When present, the block-level element(s) whose position
   * overlaps a range get a "recently changed" highlight. Optional — most
   * callers (DocumentViewer, OrderDocumentReview) don't pass this.
   */
  changedRanges?: ChangedRange[]
  /**
   * Present = the order is editable where it stands: click a paragraph, type,
   * click away (EditableParagraph.tsx). Absent = exactly the read-only
   * renderer this always was. The host says what a save is.
   */
  editing?: DocumentEditing
}

export type { DocumentEditing } from "./EditableParagraph"
export type { DocumentEdit } from "../lib/paragraph-edit"

/** node shape react-markdown passes into custom renderers: has a hast `position`. */
type MdNode = { position?: NodePosition } | undefined

function highlightClass(node: MdNode, changedRanges: ChangedRange[] | undefined, base?: string): string {
  return cn(base, isNodeChanged(node?.position, changedRanges) && DIFF_HIGHLIGHT_CLASS)
}

/**
 * markdownComponents is built per-render (memoized on changedRanges) instead
 * of being a static module-level object, so each block renderer can close
 * over the current changedRanges and check node.position against it.
 */
function getMarkdownComponents(changedRanges: ChangedRange[] | undefined): Components {
  return {
    code({ className, children, ...props }) {
      const match = /language-(\w+)/.exec(className ?? "")
      const isBlock = !("ref" in props)
      if (match && isBlock) {
        return (
          <SyntaxHighlighter style={oneLight} language={match[1]} PreTag="div" className="rounded-md text-sm">
            {String(children).replace(/\n$/, "")}
          </SyntaxHighlighter>
        )
      }
      return (
        <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
      )
    },
    h1: ({ node, children }) => (
      <h1 className={highlightClass(node, changedRanges, "text-2xl font-bold mt-8 mb-4 border-b pb-2")}>
        {children}
      </h1>
    ),
    h2: ({ node, children }) => (
      <h2 className={highlightClass(node, changedRanges, "text-xl font-semibold mt-6 mb-3")}>{children}</h2>
    ),
    h3: ({ node, children }) => (
      <h3 className={highlightClass(node, changedRanges, "text-lg font-medium mt-4 mb-2")}>{children}</h3>
    ),
    p: ({ node, children }) => <p className={highlightClass(node, changedRanges)}>{children}</p>,
    li: ({ node, children }) => <li className={highlightClass(node, changedRanges)}>{children}</li>,
    table: ({ children }) => (
      <div className="overflow-x-auto my-4">
        <table className="w-full border-collapse text-sm">{children}</table>
      </div>
    ),
    tr: ({ node, children }) => <tr className={highlightClass(node, changedRanges)}>{children}</tr>,
    th: ({ children }) => (
      <th className="border border-border px-3 py-2 text-left font-medium bg-muted">{children}</th>
    ),
    td: ({ children }) => <td className="border border-border px-3 py-2">{children}</td>,
  }
}

/**
 * Memoized with React's default shallow prop comparison (content + className
 * + changedRanges reference equality) — NOT a custom comparator that ignores
 * changedRanges. This component (specifically ReactMarkdown inside it) does
 * a full synchronous re-parse of the whole document on every render; without
 * memoization it re-parses on every unrelated parent re-render too (parent
 * components re-render often — new chat messages, unrelated state changes,
 * etc.), which for a multi-thousand-line document blocks the main thread
 * long enough to make the tab feel frozen. `changedRanges` state upstream
 * (OIOWorkspace) is only ever replaced with a genuinely new array when a new
 * commit's ranges arrive, never recreated on unrelated renders, so default
 * shallow comparison correctly skips re-parsing when nothing this component
 * actually cares about has changed.
 */
function DocumentRendererComponent({ content, className, changedRanges, editing }: DocumentRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  // "Seen" state for the navigator — dismissed manually, never on a timer
  // (highlights themselves stay in the markup until the next commit/nav away).
  const [dismissed, setDismissed] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [highlightCount, setHighlightCount] = useState(0)

  useEffect(() => {
    setDismissed(false)
    setActiveIndex(0)
  }, [changedRanges])

  // Count highlighted nodes after the markdown (re)renders so the navigator
  // badge reflects what's actually on the page, not the raw range count
  // (multiple ranges can land in the same block, or none can resolve to a
  // renderable node).
  useEffect(() => {
    if (!changedRanges?.length || dismissed) {
      setHighlightCount(0)
      return
    }
    const nodes = containerRef.current?.querySelectorAll(".diff-highlight")
    setHighlightCount(nodes?.length ?? 0)
  }, [content, changedRanges, dismissed])

  const goToChange = useCallback((nextIndex: number) => {
    const nodes = containerRef.current?.querySelectorAll<HTMLElement>(".diff-highlight")
    if (!nodes || nodes.length === 0) return
    const clamped = ((nextIndex % nodes.length) + nodes.length) % nodes.length
    setActiveIndex(clamped)
    nodes[clamped].scrollIntoView({ behavior: "smooth", block: "center" })
  }, [])

  const showNavigator = highlightCount > 0 && !dismissed

  // The parse is the expensive part, and THREE guards are load-bearing:
  //  - useDeferredValue drops the parse to deferred priority, so the click
  //    that delivered new content paints first;
  //  - SECTION CHUNKING (the 31 Aug "wait a couple of seconds before
  //    clicking Analysis" fix): a single ReactMarkdown over the whole order
  //    is ONE multi-second synchronous render React cannot interrupt even
  //    at deferred priority — any click during that window queued behind it
  //    into "Page Unresponsive". Split on top-level headings, each section
  //    its own memoized component, and React yields BETWEEN sections;
  //  - per-section memo(content) means a rewrite commit re-parses only the
  //    section that changed, not the whole document.
  const deferredContent = useDeferredValue(content)
  const sections = useMemo(() => splitMarkdownSections(deferredContent), [deferredContent])

  // PROGRESSIVE MOUNT — the actual fix for the "couple seconds then it's
  // fine" freeze. React renders all siblings in ONE synchronous pass, so
  // splitting the order into sections did not let the thread yield: the
  // whole multi-section parse still blocked, and a click on the Analysis tab
  // during that window queued into "Page Unresponsive". Instead we reveal a
  // few sections per animation frame; between frames the thread is free, so
  // the tab click (and every other interaction) processes immediately while
  // the rest of the order fills in top-to-bottom.
  const INITIAL_SECTIONS = 3
  const BATCH = 3
  const [visibleCount, setVisibleCount] = useState(INITIAL_SECTIONS)
  useEffect(() => {
    // A new document (or a rewrite commit) restarts the reveal.
    setVisibleCount(INITIAL_SECTIONS)
  }, [sections])
  useEffect(() => {
    if (visibleCount >= sections.length) return
    let raf = 0
    raf = requestAnimationFrame(() =>
      setVisibleCount((c) => Math.min(c + BATCH, sections.length)),
    )
    return () => cancelAnimationFrame(raf)
  }, [visibleCount, sections.length])

  const markdown = useMemo(
    () => (
      <>
        {sections.slice(0, visibleCount).map((section, i) => (
          <MarkdownSection
            key={i}
            content={section.text}
            startLine={section.startLine}
            startOffset={section.startOffset}
            changedRanges={changedRanges}
            editing={editing}
          />
        ))}
      </>
    ),
    [sections, visibleCount, changedRanges, editing],
  )

  return (
    <div
      className={cn(
        "prose prose-sm max-w-none relative",
        dismissed && "diff-nav-dismissed",
        className,
      )}
      ref={containerRef}
    >
      {markdown}
      {showNavigator && (
        <div className="diff-nav" data-testid="diff-nav">
          <span className="diff-nav__count">
            {highlightCount} change{highlightCount > 1 ? "s" : ""}
          </span>
          <button
            type="button"
            className="diff-nav__btn"
            onClick={() => goToChange(activeIndex + 1)}
            data-testid="diff-nav-next"
          >
            Next ▸
          </button>
          <button
            type="button"
            className="diff-nav__btn diff-nav__close"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss change highlights"
            data-testid="diff-nav-dismiss"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  )
}


/** Split a markdown document on top-level "## " headings into renderable
 *  chunks. Splitting only at line-start headings keeps every table, list and
 *  code fence intact inside its section. A document with no headings renders
 *  as one section (old behavior). */
export interface MarkdownSectionData {
  text: string
  /** 1-indexed line of this section's first line in the full document, so
   *  changed-range highlighting (which is full-document line based) can be
   *  offset back to section-local coordinates. */
  startLine: number
  /** Character offset of this section's first character in the full document:
   *  a paragraph's parse position is section-local, an edit is applied to the
   *  whole document (EditableParagraph). */
  startOffset: number
}

export function splitMarkdownSections(doc: string): MarkdownSectionData[] {
  if (!doc) return []
  const lines = doc.split("\n")
  const sections: MarkdownSectionData[] = []
  let current: string[] = []
  let sectionStart = 1
  let sectionOffset = 0
  let offset = 0
  let inFence = false
  const MAX_SECTION_LINES = 120
  lines.forEach((line, idx) => {
    if (/^\s*(```|~~~)/.test(line)) inFence = !inFence
    // Break at any ## or ### heading, OR when a headingless run grows too
    // large — never mid-fence (tables/code must stay whole).
    const atHeading = /^#{2,3}\s/.test(line)
    // A size-cap break only at a blank line, so a list/paragraph is never
    // split mid-construct (a heading is always a safe boundary).
    const tooBigAtBlank = current.length >= MAX_SECTION_LINES && line.trim() === ""
    if (!inFence && (atHeading || tooBigAtBlank) && current.length > 0) {
      sections.push({ text: current.join("\n"), startLine: sectionStart, startOffset: sectionOffset })
      current = []
      sectionStart = idx + 1
      sectionOffset = offset
    }
    current.push(line)
    offset += line.length + 1
  })
  if (current.length > 0) sections.push({ text: current.join("\n"), startLine: sectionStart, startOffset: sectionOffset })
  return sections
}

/** One section of the order, parsed independently so React can yield between
 *  sections and a rewrite commit re-parses only what changed. Changed ranges
 *  are shifted into this section's local line coordinates so highlighting
 *  still lands on the right blocks. */
const MarkdownSection = memo(function MarkdownSection({
  content,
  startLine,
  startOffset,
  changedRanges,
  editing,
}: {
  content: string
  startLine: number
  startOffset: number
  changedRanges: ChangedRange[] | undefined
  editing: DocumentEditing | undefined
}) {
  const localRanges = useMemo(() => {
    if (!changedRanges?.length) return changedRanges
    const off = startLine - 1
    return changedRanges.map((r) => ({
      ...r,
      start_line: r.start_line - off,
      end_line: r.end_line - off,
    }))
  }, [changedRanges, startLine])
  // Editable: the paragraph is the ONE stable component (it must not remount
  // under an open editor when this map is rebuilt), fed through context.
  const components = useMemo(() => {
    const base = getMarkdownComponents(localRanges)
    return editing ? { ...base, p: EditableParagraph as Components["p"] } : base
  }, [localRanges, editing])
  const sectionEdit = useMemo(
    () => (editing ? { text: content, startOffset, editing, changedRanges: localRanges } : null),
    [content, startOffset, editing, localRanges],
  )
  const rendered = (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  )
  return sectionEdit ? <SectionEditContext.Provider value={sectionEdit}>{rendered}</SectionEditContext.Provider> : rendered
})

export const DocumentRenderer = memo(DocumentRendererComponent)
